import AssetTypeSelectCombo, {
  meterOptions,
} from "components/Asset/AssetTypeSelectCombo";
import NewAssetUseCaseSelect from "components/Asset/NewAssetUseCaseSelect";
import AssetPositionGroupSelectCombo from "components/AssetPositionGroup/AssetPositionGroupSelectCombo";
import PeriodSelectCombo from "components/DateTime/PeriodSelectCombo";
import {
  ButtonGroupField,
  type OptionsList,
} from "components/Forms/ButtonGroup";
import type { LabelProps } from "components/Forms/NewLabelWrapper";
import type { Value } from "components/Forms/SelectCombo/SelectCombo";
import { Col, Container } from "components/Theme/grid";
import AssetAggregationSelectCombo from "components/TimeSeries/AssetAggregationSelectCombo/AssetAggregationSelectCombo";
import AssetParameterSelectCombo from "components/TimeSeries/AssetParameterSelectCombo/AssetParameterSelectCombo";
import ParameterUnitsSelect from "components/TimeSeries/ParameterUnitsSelect";
import {
  type AssetType,
  type AssetTypeList,
  getAggregations,
  getFields,
} from "components/TimeSeries/model";
import {
  type UnitsValue,
  aggregationSupportsUnits,
  getDefaultUnitsForParameter,
} from "components/TimeSeries/parameterUnits";
import { useFormikContext } from "formik";
import {
  type AssetUseCaseEnum,
  type GroupBy,
  type Period,
  type ReportView,
  energyUnitsEnum,
  groupByEnum,
  periodEnum,
  reportViewEnum,
} from "kubb";
import { get, isNil } from "lodash-es";
import type React from "react";
import type { OnChangeValue } from "react-select";
import * as Yup from "yup";

export interface AggregatedTimeSeriesQuerySubFormProps {
  wide?: boolean;
  showErrors?: boolean;
  submitButton?: React.ReactNode;
  namespace: string;
  disabled?: boolean;
  showAssetPositionGroupSelect?: boolean;
  showassetUseCaseEnum?: boolean;
  showGroup?: boolean;
  showGroupByScheme?: boolean;
  showGroupByGroup?: boolean;
}

export interface AggregatedTimeSeriesQuerySubFormValues {
  asset_type?: AssetTypeList;
  parameter: string;
  aggregation: string;
  period: Period;
  group?: string;
  view_by?: ReportView;
  group_by?: GroupBy;
  use_case?: AssetUseCaseEnum[];
  units?: UnitsValue;
}

export const AggregatedTimeSeriesQueryDefaultValues: AggregatedTimeSeriesQuerySubFormValues =
  {
    asset_type: meterOptions,
    parameter: "Energy (Heating)",
    aggregation: "last",
    period: periodEnum.DAILY,
    view_by: reportViewEnum.ASSET_POSITION,
    units: energyUnitsEnum.kWh,
  };

function handleAssetTypeChange(
  setParameter: (v: string | null) => Promise<any>,
  setAggregation: (v: string | null) => Promise<any>,
  setUnits: (v: UnitsValue) => Promise<any>,
): (
  values: AggregatedTimeSeriesQuerySubFormValues,
  change: OnChangeValue<Value, boolean>,
) => Promise<any> {
  return (values, change) => {
    const [newParameter, newAggregation] = getNewParameterAndAggregation(
      values.parameter || null,
      values.aggregation || null,
      change as string[] | null,
    );
    const effectiveAggregation = newAggregation || values.aggregation;
    const newUnits =
      newParameter &&
      effectiveAggregation &&
      aggregationSupportsUnits(effectiveAggregation)
        ? getDefaultUnitsForParameter(newParameter)
        : null;
    return setAggregation(newAggregation)
      .then(() => setParameter(newParameter))
      .then(() => setUnits(newUnits));
  };
}

function getNewParameterAndAggregation(
  currentParameter: string | null,
  currentAggregation: string | null,
  newAssetTypeList: string[] | null,
): (string | null)[] {
  if (isNil(newAssetTypeList) || (newAssetTypeList as Value[]).length === 0) {
    return [null, null];
  } else {
    const newParameters = getFields(newAssetTypeList as AssetTypeList);
    if (currentParameter) {
      if (newParameters.indexOf(currentParameter) === -1) {
        return [newParameters[0], getAggregations(newParameters[0])[0]];
      } else {
        if (currentAggregation) {
          const newAggregations = getAggregations(currentParameter);
          if (newAggregations.indexOf(currentAggregation) === -1) {
            return [currentParameter, newAggregations[0]];
          }
        }
      }
    }
  }
  return [currentParameter, currentAggregation];
}

function handleParameterChange(
  setAggregation: (v: string | null) => Promise<any>,
  setUnits: (v: UnitsValue) => Promise<any>,
): (
  values: AggregatedTimeSeriesQuerySubFormValues,
  change: OnChangeValue<Value, boolean>,
) => Promise<any> {
  return (values, change) => {
    const newParameter = change as string | null;
    const [_, newAggregation] = getNewParameterAndAggregation(
      newParameter,
      values.aggregation || null,
      values.asset_type as string[] | null,
    );
    const effectiveAggregation = newAggregation || values.aggregation;
    const newUnits =
      newParameter &&
      effectiveAggregation &&
      aggregationSupportsUnits(effectiveAggregation)
        ? getDefaultUnitsForParameter(newParameter)
        : null;
    return setAggregation(newAggregation).then(() => setUnits(newUnits));
  };
}

export function handleAggregationChange(
  setUnits: (v: UnitsValue) => Promise<any>,
): (
  values: AggregatedTimeSeriesQuerySubFormValues,
  change: OnChangeValue<Value, boolean>,
) => Promise<any> {
  return (values, change) => {
    const newAggregation = change as string | null;
    if (newAggregation && !aggregationSupportsUnits(newAggregation)) {
      return setUnits(null);
    }
    if (values.parameter) {
      return setUnits(getDefaultUnitsForParameter(values.parameter));
    }
    return Promise.resolve();
  };
}

export const AggregatedTimeSeriesQuerySubFormSchema = Yup.object().shape({
  period: Yup.string().nullable().required("Required"),
  asset_type: Yup.array()
    .of(Yup.string().required())
    .nullable()
    .min(1)
    .required("Required"),
  aggregation: Yup.string().nullable().required("Required"),
  parameter: Yup.string().nullable().required("Required"),
  units: Yup.string().nullable(),
});

function AggregatedTimeSeriesQuerySubForm(
  props: AggregatedTimeSeriesQuerySubFormProps,
) {
  const {
    namespace,
    wide = false,
    showErrors = true,
    disabled = false,
  } = props;

  const labelDefaults: Partial<LabelProps> = { size: "sm", showErrors };

  const context = useFormikContext<{
    namespace: AggregatedTimeSeriesQuerySubFormValues;
  }>();

  const { setFieldValue } = context;

  const values: AggregatedTimeSeriesQuerySubFormValues = get(
    context.values,
    namespace,
  );

  const withNamespace = (fieldName: string): string => {
    return namespace ? `${namespace}.${fieldName}` : fieldName;
  };

  return (
    <>
      {values && (
        <Container>
          <Col md={wide ? 6 : 12} sm={12}>
            <Container gap={3}>
              <Col sm={wide ? (props.showassetUseCaseEnum ? 8 : 12) : 12}>
                <AssetTypeSelectCombo
                  fieldName={withNamespace("asset_type")}
                  onChange={(change) => {
                    return handleAssetTypeChange(
                      (v) => setFieldValue(withNamespace("parameter"), v),
                      (v) => setFieldValue(withNamespace("aggregation"), v),
                      (v) => setFieldValue(withNamespace("units"), v),
                    )(values, change);
                  }}
                  includeNonCreateables={false}
                  includeWirelessCluster={false}
                  allowUnknown={false}
                  isMulti={true}
                  disabled={disabled}
                  label={{ label: "Asset Type", ...labelDefaults }}
                />
              </Col>
              {props.showassetUseCaseEnum && (
                <Col sm={wide ? 4 : 12}>
                  <NewAssetUseCaseSelect
                    fieldName={withNamespace("use_case")}
                    label={{ label: "Asset Use Cases", size: "sm", showErrors }}
                    isMulti={true}
                  />
                </Col>
              )}
              <Col sm={wide ? 3 : 12}>
                <AssetParameterSelectCombo
                  fieldName={withNamespace("parameter")}
                  onChange={(change) => {
                    return handleParameterChange(
                      (v) => setFieldValue(withNamespace("aggregation"), v),
                      (v) => setFieldValue(withNamespace("units"), v),
                    )(values, change);
                  }}
                  clearable={false}
                  disabled={
                    isNil(values?.asset_type) ||
                    (values.asset_type as AssetType[]).length === 0 ||
                    disabled
                  }
                  assetType={values.asset_type}
                  isMulti={false}
                  label={{ label: "Parameter", ...labelDefaults }}
                />
              </Col>
              <Col sm={wide ? 3 : 12}>
                <AssetAggregationSelectCombo
                  fieldName={withNamespace("aggregation")}
                  clearable={false}
                  isMulti={false}
                  label={{ label: "Aggregation", ...labelDefaults }}
                  assetParameter={values.parameter || ""}
                  disabled={isNil(values.parameter) || disabled}
                  onChange={(change) => {
                    return handleAggregationChange((v) =>
                      setFieldValue(withNamespace("units"), v),
                    )(values, change);
                  }}
                />
              </Col>
              <Col sm={wide ? 3 : 12}>
                <ParameterUnitsSelect
                  fieldName={withNamespace("units")}
                  parameter={values.parameter || null}
                  aggregation={values.aggregation || null}
                  disabled={isNil(values?.parameter) || disabled}
                  label={{ label: "Units", ...labelDefaults }}
                />
              </Col>
              <Col sm={wide ? 3 : 12}>
                <PeriodSelectCombo
                  fieldName={withNamespace("period")}
                  isMulti={false}
                  clearable={false}
                  disabled={disabled}
                  label={{ label: "Period", ...labelDefaults }}
                />
              </Col>
            </Container>
          </Col>
          <Col sm={12} md={wide ? 6 : 12}>
            <Container gap={3}>
              {(props.showAssetPositionGroupSelect === undefined ||
                props.showAssetPositionGroupSelect) && (
                <Col sm={wide ? 5 : 12} lg={wide ? 6 : 12}>
                  <AssetPositionGroupSelectCombo
                    fieldName={withNamespace("group")}
                    isMulti={false}
                    clearable={false}
                    disabled={disabled}
                    label={{ label: "Group", ...labelDefaults }}
                  />
                </Col>
              )}
              <Col sm={wide ? 7 : 12} lg={wide ? 6 : 12}>
                {props.showGroup ? (
                  <ButtonGroupField
                    fieldName={withNamespace("group_by")}
                    label={{ label: "Group By", ...labelDefaults }}
                    disabled={disabled}
                    options={
                      [
                        { label: "None", value: groupByEnum.NONE },
                        props.showGroupByScheme
                          ? {
                              label: "Scheme",
                              value: groupByEnum.SCHEME,
                            }
                          : undefined,
                        props.showGroupByGroup
                          ? {
                              label: "Group",
                              value: groupByEnum.GROUP_ALL,
                            }
                          : undefined,
                      ].filter((v) => !isNil(v)) as OptionsList
                    }
                    fullWidth={true}
                  />
                ) : (
                  <ButtonGroupField
                    fieldName={withNamespace("view_by")}
                    label={{ label: "View By", ...labelDefaults }}
                    disabled={disabled}
                    options={[
                      { label: "Asset", value: reportViewEnum.ASSET },
                      {
                        label: "Asset Position",
                        value: reportViewEnum.ASSET_POSITION,
                      },
                    ]}
                    fullWidth={true}
                  />
                )}
              </Col>
              {props.submitButton}
            </Container>
          </Col>
        </Container>
      )}
    </>
  );
}

export default AggregatedTimeSeriesQuerySubForm;
